import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Download, ExternalLink, Package, ShieldQuestion, Trash2, X } from 'lucide-react';
import {
  type AppRow,
  type InstalledAppRow,
  type ReviewRow,
  decidePublication,
  installApp,
  listInstalledApps,
  listMarketplaceApps,
  listPendingPublications,
  uninstallApp,
} from '@/api/marketplace';
import { errorDetail } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/cn';

type Tab = 'available' | 'installed' | 'reviews';

/** /admin/marketplace — browse/install published apps + (platform) review queue. */
export function MarketplacePage() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.currentRole ?? s.user?.role);
  const isPlatformAdmin = role === 'PLATFORM_ADMIN';

  const [tab, setTab] = useState<Tab>('available');
  const [apps, setApps] = useState<AppRow[]>([]);
  const [installed, setInstalled] = useState<InstalledAppRow[]>([]);
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const tasks: [Promise<AppRow[]>, Promise<InstalledAppRow[]>, Promise<ReviewRow[]>] = [
      listMarketplaceApps(),
      listInstalledApps(),
      isPlatformAdmin ? listPendingPublications() : Promise.resolve<ReviewRow[]>([]),
    ];
    Promise.all(tasks)
      .then(([a, i, r]) => {
        setApps(a);
        setInstalled(i);
        setReviews(r);
      })
      .catch((err) => setError(errorDetail(err)))
      .finally(() => setLoading(false));
  }, [isPlatformAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const installedClientIds = useMemo(
    () => new Set(installed.map((i) => i.oauthClientId)),
    [installed],
  );

  async function handleInstall(app: AppRow) {
    if (!confirm(t('appMarketplace.installConfirm', { name: app.name }))) return;
    setBusyId(app.id);
    setError(null);
    try {
      await installApp(app.id, app.allowedScopes);
      load();
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusyId(null);
    }
  }

  async function handleUninstall(item: InstalledAppRow) {
    if (!confirm(t('appMarketplace.uninstallConfirm', { name: item.appName }))) return;
    setBusyId(item.installId);
    setError(null);
    try {
      await uninstallApp(item.installId);
      load();
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDecision(review: ReviewRow, approved: boolean, notes: string) {
    setBusyId(review.id);
    setError(null);
    try {
      await decidePublication(review.id, approved, notes.trim() || null);
      load();
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusyId(null);
    }
  }

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'available', label: t('appMarketplace.tabs.available'), count: apps.length },
    { key: 'installed', label: t('appMarketplace.tabs.installed'), count: installed.length },
    ...(isPlatformAdmin
      ? [{ key: 'reviews' as Tab, label: t('appMarketplace.tabs.reviews'), count: reviews.length }]
      : []),
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
          {t('appMarketplace.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
          {t('appMarketplace.subtitle')}
        </p>
      </div>

      <div className="flex gap-1 border-b border-[var(--color-lop-border)]">
        {tabs.map((tabItem) => (
          <button
            key={tabItem.key}
            type="button"
            onClick={() => setTab(tabItem.key)}
            data-testid={`marketplace-tab-${tabItem.key}`}
            className={cn(
              'px-3 py-2 text-sm font-medium transition-colors',
              tab === tabItem.key
                ? 'border-b-2 border-primary text-[var(--color-lop-text-primary)]'
                : 'text-muted-foreground hover:text-[var(--color-lop-text-primary)]',
            )}
          >
            {tabItem.label} ({tabItem.count})
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : tab === 'available' ? (
        <AvailableTab
          apps={apps}
          installedClientIds={installedClientIds}
          busyId={busyId}
          onInstall={handleInstall}
        />
      ) : tab === 'installed' ? (
        <InstalledTab installed={installed} busyId={busyId} onUninstall={handleUninstall} />
      ) : (
        <ReviewsTab reviews={reviews} busyId={busyId} onDecision={handleDecision} />
      )}
    </div>
  );
}

function AppLogo({ logoUrl, name, size }: { logoUrl: string | null; name: string; size: 'sm' | 'md' }) {
  const dim = size === 'md' ? 'h-12 w-12' : 'h-10 w-10';
  const icon = size === 'md' ? 'h-6 w-6' : 'h-5 w-5';
  if (logoUrl) {
    return <img src={logoUrl} alt={name} className={cn('shrink-0 rounded', dim)} />;
  }
  return (
    <div className={cn('flex shrink-0 items-center justify-center rounded bg-muted', dim)}>
      <Package className={cn('text-muted-foreground', icon)} />
    </div>
  );
}

function ScopeChips({ scopes }: { scopes: string[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {scopes.map((s) => (
        <code
          key={s}
          className="rounded bg-[var(--color-lop-bg-card-hi)] px-1.5 py-0.5 font-mono text-[11px] text-[var(--color-lop-text-secondary)]"
        >
          {s}
        </code>
      ))}
    </div>
  );
}

function AvailableTab({
  apps,
  installedClientIds,
  busyId,
  onInstall,
}: {
  apps: AppRow[];
  installedClientIds: Set<string>;
  busyId: string | null;
  onInstall: (app: AppRow) => void;
}) {
  const { t } = useTranslation();
  if (apps.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {t('appMarketplace.emptyAvailable')}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {apps.map((app) => {
        const isInstalled = installedClientIds.has(app.id);
        return (
          <Card key={app.id} className="p-4" data-testid="marketplace-app-row">
            <div className="flex items-start gap-3">
              <AppLogo logoUrl={app.logoUrl} name={app.name} size="md" />
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-[var(--color-lop-text-primary)]">{app.name}</h3>
                <code className="font-mono text-xs text-muted-foreground">{app.clientId}</code>
                {app.allowedScopes.length > 0 && (
                  <>
                    <div className="mt-2 text-[11px] font-medium text-muted-foreground">
                      {t('appMarketplace.requestedScopes')}
                    </div>
                    <ScopeChips scopes={app.allowedScopes} />
                  </>
                )}
                <div className="mt-3 flex items-center gap-2">
                  {isInstalled ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400">
                      <Check className="h-3.5 w-3.5" />
                      {t('appMarketplace.alreadyInstalled')}
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => onInstall(app)}
                      disabled={busyId === app.id}
                      data-testid="marketplace-install"
                    >
                      <Download className="mr-1 h-3.5 w-3.5" />
                      {busyId === app.id ? t('appMarketplace.installing') : t('appMarketplace.install')}
                    </Button>
                  )}
                  {app.homepageUrl && (
                    <a
                      href={app.homepageUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      title={t('appMarketplace.homepage')}
                    >
                      <Button size="sm" variant="ghost">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  )}
                  {app.privacyPolicyUrl && (
                    <a
                      href={app.privacyPolicyUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      {t('appMarketplace.privacyPolicy')}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function InstalledTab({
  installed,
  busyId,
  onUninstall,
}: {
  installed: InstalledAppRow[];
  busyId: string | null;
  onUninstall: (item: InstalledAppRow) => void;
}) {
  const { t } = useTranslation();
  if (installed.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {t('appMarketplace.emptyInstalled')}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {installed.map((item) => (
        <Card key={item.installId} className="flex items-start gap-3 p-4" data-testid="marketplace-installed-row">
          <AppLogo logoUrl={item.logoUrl} name={item.appName} size="sm" />
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-[var(--color-lop-text-primary)]">{item.appName}</h3>
            <code className="font-mono text-xs text-muted-foreground">{item.clientId}</code>
            {item.grantedScopes.length > 0 && (
              <>
                <div className="mt-1 text-[11px] font-medium text-muted-foreground">
                  {t('appMarketplace.grantedScopes')}
                </div>
                <ScopeChips scopes={item.grantedScopes} />
              </>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            title={t('appMarketplace.uninstall')}
            onClick={() => onUninstall(item)}
            disabled={busyId === item.installId}
            data-testid="marketplace-uninstall"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </Card>
      ))}
    </div>
  );
}

function ReviewsTab({
  reviews,
  busyId,
  onDecision,
}: {
  reviews: ReviewRow[];
  busyId: string | null;
  onDecision: (review: ReviewRow, approved: boolean, notes: string) => void;
}) {
  const { t } = useTranslation();
  if (reviews.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {t('appMarketplace.emptyReviews')}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {reviews.map((review) => (
        <ReviewCard key={review.id} review={review} busyId={busyId} onDecision={onDecision} />
      ))}
    </div>
  );
}

function ReviewCard({
  review,
  busyId,
  onDecision,
}: {
  review: ReviewRow;
  busyId: string | null;
  onDecision: (review: ReviewRow, approved: boolean, notes: string) => void;
}) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const busy = busyId === review.id;
  return (
    <Card className="p-4" data-testid="marketplace-review-row">
      <div className="flex items-start gap-3">
        <ShieldQuestion className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-[var(--color-lop-text-primary)]">{review.appName}</h3>
          <code className="font-mono text-xs text-muted-foreground">{review.clientId}</code>
          <div className="mt-1 text-xs text-muted-foreground">
            {t('appMarketplace.submittedAt')}: {review.submittedAt}
          </div>
          {review.notes && (
            <div className="mt-1 text-xs text-muted-foreground">
              {t('appMarketplace.notes')}: {review.notes}
            </div>
          )}
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-3"
            placeholder={t('appMarketplace.notesPlaceholder')}
          />
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              onClick={() => onDecision(review, true, notes)}
              disabled={busy}
              data-testid="marketplace-approve"
            >
              <Check className="mr-1 h-3.5 w-3.5" />
              {t('appMarketplace.approve')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDecision(review, false, notes)}
              disabled={busy}
              data-testid="marketplace-reject"
            >
              <X className="mr-1 h-3.5 w-3.5" />
              {t('appMarketplace.reject')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
